import { z } from 'zod';
import { VollWalletAllocationInputSchema } from './voll_wallet_allocation.js';
import { VollWalletRefSchema } from './voll_wallet_pix.js';

/** O estado do cartão como a tela age sobre ele: `active` compra e pode ser bloqueado,
 *  `locked` foi bloqueado pelo viajante e pode ser desbloqueado, `unavailable` é qualquer outro
 *  status do emissor (cancelado, expirado) — sem ação, ao contrário do app legado, que o
 *  mostrava como bloqueado com "Desbloquear". */
export const VollWalletCardStateSchema = z.enum(['active', 'locked', 'unavailable']);
export type VollWalletCardState = z.infer<typeof VollWalletCardStateSchema>;

/** A alocação como rótulo do cartão lido: o nome vem do cadastro e pode estar vazio, e um rótulo
 *  não derruba a leitura do cartão (#6673). Os schemas de entrada seguem exigindo o nome. */
const VollWalletCardRefSchema = VollWalletRefSchema.extend({ name: z.string() });

/**
 * `GET /voll-wallet/card` — o cartão VOLL Pay do viajante, SEM os dados sensíveis: `none` quando
 * ainda não há configuração (o onboarding ativa); senão o cartão, a alocação em que ele cobra e
 * as carteiras de onde ele debita.
 */
export const VollWalletCardSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('none'),
      /** A alocação do cadastro com que o onboarding ativa o cartão; `null` = sem como ativar. */
      company: VollWalletRefSchema.nullable(),
      costCenter: VollWalletRefSchema.nullable(),
    })
    .strict(),
  z
    .object({
      status: z.literal('ready'),
      state: VollWalletCardStateSchema,
      holder: z.string().nullable(),
      brand: z.string().nullable(),
      /** "MM/AA". */
      expiration: z.string().nullable(),
      virtual: z.boolean(),
      company: VollWalletCardRefSchema,
      costCenter: VollWalletCardRefSchema,
      project: VollWalletCardRefSchema.nullable(),
      /** Os nomes das carteiras que aceitam cartão: é delas que a compra debita. */
      wallets: z.array(z.string()),
      /** O cartão na Stark (o `card.id` da configuração do wallets-api) e os 4 últimos dígitos:
       *  com os dois, o app adiciona o cartão à carteira digital do aparelho. Ausentes (BFF
       *  anterior ou cadastro sem eles) = sem carteira digital. */
      providerId: z.string().nullable().default(null),
      last4: z.string().nullable().default(null),
    })
    .strict(),
]);
export type VollWalletCard = z.infer<typeof VollWalletCardSchema>;

/** `POST /voll-wallet/card/setup` — ativa o cartão na alocação escolhida (ausente = a do
 *  cadastro). */
export const VollWalletCardSetupRequestSchema = z
  .object({ allocation: VollWalletAllocationInputSchema.optional() })
  .strict();
export type VollWalletCardSetupRequest = z.infer<typeof VollWalletCardSetupRequestSchema>;

/** `POST /voll-wallet/card/allocation` — troca a empresa e o centro de custo (e o projeto) em
 *  que o cartão já configurado cobra ("Dados corporativos"). Responde o cartão. */
export const VollWalletCardAllocationRequestSchema = z
  .object({ allocation: VollWalletAllocationInputSchema })
  .strict();
export type VollWalletCardAllocationRequest = z.infer<typeof VollWalletCardAllocationRequestSchema>;

/** As regras da senha do cartão, as do app legado (`CardPinValidation` do `wallet_card`): 4
 *  dígitos, nenhum dígito igual ao vizinho ("1123") e nenhum vizinho em sequência, para cima ou
 *  para baixo ("1357" passa, "1243" não). */
export const VollWalletCardPinRuleSchema = z.enum(['length', 'repeating', 'sequential']);
export type VollWalletCardPinRule = z.infer<typeof VollWalletCardPinRuleSchema>;

/** As regras que a senha (só dígitos) ainda quebra; vazia = senha válida. */
export function cardPinBreaks(pin: string): VollWalletCardPinRule[] {
  // Só os dígitos contam para as regras de vizinhança; um caractere a mais quebra a de tamanho.
  const digits = (pin.match(/\d/g) ?? []).map(Number);
  const pairs = digits.slice(1).map((d, i) => Math.abs(d - (digits[i] ?? d)));
  return [
    ...(/^\d{4}$/.test(pin) ? [] : (['length'] as const)),
    ...(pairs.includes(0) ? (['repeating'] as const) : []),
    ...(pairs.includes(1) ? (['sequential'] as const) : []),
  ];
}

/** `POST /voll-wallet/card/pin` — a nova senha do cartão; responde 204. */
export const VollWalletCardPinRequestSchema = z
  .object({ pin: z.string().refine((pin) => cardPinBreaks(pin).length === 0) })
  .strict();
export type VollWalletCardPinRequest = z.infer<typeof VollWalletCardPinRequestSchema>;

/** `POST /voll-wallet/card/reveal` — número, validade e CVV em claro. A tela os mostra por
 *  30 segundos e nunca os guarda; o BFF não os loga e responde `no-store`. */
export const VollWalletCardDataSchema = z
  .object({
    number: z.string().regex(/^\d{12,19}$/),
    expiration: z.string().nullable(),
    cvv: z.string().regex(/^\d{3,4}$/),
  })
  .strict();
export type VollWalletCardData = z.infer<typeof VollWalletCardDataSchema>;

export const VollWalletCardErrorSchema = z.discriminatedUnion('error', [
  z.object({ error: z.literal('card_not_found') }).strict(),
  z.object({ error: z.literal('card_unavailable') }).strict(),
  z.object({ error: z.literal('allocation_missing') }).strict(),
  /** A empresa ou o centro de custo escolhido não é permitido ao viajante. */
  z.object({ error: z.literal('allocation_invalid') }).strict(),
  /** A senha quebra uma das regras (`cardPinBreaks`). */
  z.object({ error: z.literal('pin_invalid') }).strict(),
]);
export type VollWalletCardError = z.infer<typeof VollWalletCardErrorSchema>;

/** `POST /voll-wallet/card/block` e `/unblock` — o estado do cartão depois da troca. */
export const VollWalletCardStateResponseSchema = z
  .object({ state: VollWalletCardStateSchema })
  .strict();
